import { useState } from "react"
import { Button } from "primereact/button"
import { InputText } from "primereact/inputtext"
import { InputTextarea } from "primereact/inputtextarea"
import { Dropdown } from "primereact/dropdown"

import {
  User,
  Mail,
  Phone,
  HelpCircle,
  MessageSquare,
  CalendarCheck,
  Users,
  Target,
  Clock,
  Briefcase,
  Shield,
  LineChart,
} from "lucide-react"
import { createBooking, getIpAddress } from "./api/booking"

export function UserForm({ selectedSlot, onSubmit, categoryInfo, config }) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formErrors, setFormErrors] = useState({})
  const [formValues, setFormValues] = useState({});

  const getFieldIcon = (name) => {
    switch (name) {
      case "name":
        return <User className="w-8 h-8" />
      case "email":
        return <Mail className="w-8 h-8" />
      case "phone":
        return <Phone className="w-8 h-8" />
      case "familySize":
        return <Users className="w-8 h-8" />
      case "age":
        return <Target className="w-8 h-8" />
      case "investmentHorizon":
        return <Clock className="w-8 h-8" />
      case "investmentAmount":
        return <Briefcase className="w-8 h-8" />
      case "riskTolerance":
        return <Shield className="w-8 h-8" />
      case "referralSource":
        return <HelpCircle className="w-8 h-8" />
      case "query":
        return <MessageSquare className="w-8 h-8" />
      default:
        return <LineChart className="w-8 h-8" />
    }
  }

  const validateField = (element, value) => {
    if (element.required && !value) {
      return `${element.display_name} is required`
    }
    if (element.validation?.pattern && value) {
      const regex = new RegExp(element.validation.pattern)
      if (!regex.test(value)) {
        return element.validation.message || `Invalid ${element.display_name}`
      }
    }
    return null
  }

  const handleInputChange = (name, value) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    const errors = {}

    try {
      const formData = new FormData(e.currentTarget)
      const formValues = {}

      // Validate all fields
      for (const element of categoryInfo.formElements) {
        const value = formData.get(element.name)
        formValues[element.name] = value

        const error = validateField(element, value)
        if (error) {
          errors[element.name] = error
        }
      }

      if (Object.keys(errors).length > 0) {
        setFormErrors(errors)
        return
      }

      const ipAddress = await getIpAddress(config.PY_API_URL)

      console.log("selectedSlot", selectedSlot)
      const bookingDetails = {
        ...formValues,
        formId: categoryInfo.id,
        browserInfo: {
          userAgent: navigator.userAgent,
          language: navigator.language,
          platform: navigator.platform,
        },
        slotId: selectedSlot?.slotId,
        date: selectedSlot?.date,
        ipAddress,
      }

      const result = await createBooking(config.PY_API_URL, bookingDetails)
      if (result.status === "success") {
        onSubmit(bookingDetails)
      }
    } catch (error) {
      console.error("Failed to submit booking:", error)
      setFormErrors({
        submit: "Failed to submit booking. Please try again.",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const renderFormElement = (element) => {
    const commonProps = {
      id: element.name,
      name: element.name,
      required: element.required,
      "aria-invalid": !!formErrors[element.name],
      "aria-describedby": formErrors[element.name] ? `${element.name}-error` : undefined,
      className: `${element.class_name || ""} border-gray-200 w-full`,
    }
    const widthClass = element.width === "half" ? "md:col-span-1" : "md:col-span-2"
    const icon = getFieldIcon(element.name)
    return (
      <div key={element.name} className={`space-y-2 md:col-12 lg:col-12 ${widthClass}`}>
        {categoryInfo.show_input_labels && (
          <label htmlFor={element.name} className="text-sm text-gray-600 flex items-center gap-2">
            <span className="text-blue-600">{icon}</span>
            {element.display_name}
            {element.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        {element.type === "textarea" && (
          <InputTextarea {...commonProps} placeholder={`Enter your ${element.display_name.toLowerCase()}`} />
        )}
        {element.type === "select" && (
          <select
            {...commonProps}
            value={formValues[element.name] || ""}
            onChange={(e) => handleInputChange(element.name, e.target.value)}
            className={`w-full p-2 border border-gray-300 rounded-md focus:ring focus:ring-blue-300 ${commonProps.className}`}
          >
            <option value="" disabled>
              {`Select ${element.display_name.toLowerCase()}`}
            </option>
            {(() => {
              try {
                const options = JSON.parse(element.options);
                return options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ));
              } catch (error) {
                return <option disabled>Error loading options</option>;
              }
            })()}
          </select>
        )}

        {(element.type === "text" || element.type === "email" || element.type === "tel" || element.type === "number" || element.type === "password") && (
          <InputText {...commonProps} type={element.type} placeholder={`Enter your ${element.display_name.toLowerCase()}`} />
        )}

        {formErrors[element.name] && (
          <p id={`${element.name}-error`} className="text-sm text-red-500">
            {formErrors[element.name]}
          </p>
        )}
      </div>
    )
  }

  return (
    <div>
      <form onSubmit={handleSubmit} className="space-y-6 grid-">
        <div className={`grid- ${categoryInfo?.skip_slot ? 'col-md-12' : 'md:grid-cols-2'} gap-4 col-12 ml-0`} style={{padding: '0px'}}>
          {categoryInfo.formElements.sort((a, b) => a.sort_order - b.sort_order).map(renderFormElement)}
        </div>

        {formErrors.submit && <p className="text-sm text-red-500 text-center">{formErrors.submit}</p>}

        <div style={{padding: "8px"}}>
          <Button 
            style={{height: "50px"}} 
            type="submit" 
            className="w-full p-button-primary" 
            disabled={isSubmitting}
          >
            <CalendarCheck className="w-1 h-1 mr-2" style={{position: "absolute", left: "10px", top: "10px"}} />
            <span>{isSubmitting ? "Confirming..." : categoryInfo.form_button_text || "Confirm Booking"}</span>
          </Button>
        </div>
      </form>
    </div>
  )
}
